import React, { useState } from "react";
import { Link } from "react-router-dom";
import { usePricing } from "@d-m-organization/pricing-ui";
import { ProductIcon } from "./ProductIcon.js";
import { PRODUCT_COLORS } from "../config/products.js";
import type { ProductIconType } from "../config/products.js";
import { buildPricingViewModel } from "../lib/pricingViewModel.js";
import { salesMailto } from "../lib/contactLinks.js";

// ── Tokens ─────────────────────────────────────────────────────────────────
const C = {
  purple: "#351DBD",
  purpleDark: "#1a0f6e",
  orange: "#FF861D",
  blue: "#349DFF",
  purpleLight: "#EDE9FC",
  orangeLight: "#FFF0E2",
  blueLight: "#E5F2FF",
  navy: "#261F59",
  textStrong: "#2b2e44",
  textMuted: "#6b7084",
  textFaint: "#9096a8",
  muted: "#E3EBF6",
  mutedSoft: "#EEF1F7",
  bg: "#f0eef6",
  bgTint: "#F7F6FB",
  white: "#ffffff",
  success: "#22c55e",
  successLight: "#dcfce7",
  successText: "#166534",
} as const;

// ── Static per-product display config ──────────────────────────────────────
// Catalog gives us productId + displayName + price; this map provides the
// non-pricing display details (icon, sub-tagline) that are presentational
// and shouldn't live in the catalog. Falls back to defaults when a new
// product appears in the catalog before this map is updated.
type DisplayMeta = {
  icon: ProductIconType;
  sub: string;
  color: string;
  tint: string;
};

const PRODUCT_DISPLAY: Record<string, DisplayMeta> = {
  qr:        { icon: "qr",       sub: "QR codes for any link",   color: PRODUCT_COLORS.qr.accent,          tint: PRODUCT_COLORS.qr.accentLight },
  invoice:   { icon: "file",     sub: "Send invoices, get paid", color: PRODUCT_COLORS.invoice.accent,     tint: PRODUCT_COLORS.invoice.accentLight },
  planner:   { icon: "calendar", sub: "Projects & events",       color: PRODUCT_COLORS.planner.accent,     tint: PRODUCT_COLORS.planner.accentLight },
  link:      { icon: "link",     sub: "One link for your brand", color: PRODUCT_COLORS["link-page"].accent, tint: PRODUCT_COLORS["link-page"].accentLight },
  "link-page": { icon: "link",   sub: "One link for your brand", color: PRODUCT_COLORS["link-page"].accent, tint: PRODUCT_COLORS["link-page"].accentLight },
  menu:      { icon: "menu",     sub: "Scannable venue menus",   color: PRODUCT_COLORS.menu.accent,        tint: PRODUCT_COLORS.menu.accentLight },
  events:    { icon: "event",    sub: "Public pages with RSVP",  color: PRODUCT_COLORS.events.accent,      tint: PRODUCT_COLORS.events.accentLight },
};

const DEFAULT_DISPLAY: DisplayMeta = {
  icon: "qr",
  sub: "",
  color: C.purple,
  tint: C.purpleLight,
};

// ── Check icon ─────────────────────────────────────────────────────────────
function CheckIcon({ color = "currentColor", size = 16 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PRICING SECTION — V2: catalog-driven (HD-99)
//
// Bundle removal note (HD-???):
// The bundle column was removed from the rendered UI; only the à-la-carte
// path remains. Several helpers below (TierCard, TierTop, TierDivider,
// TierFeatures, CheckIcon, DeltaSticker, ToggleBtn) are temporarily dormant.
// They're kept in place so reverting is a few-line restore — do NOT delete
// them as dead code without a follow-up ticket confirming bundles are gone
// for good.
// ═════════════════════════════════════════════════════════════════════════════
export function PricingSection() {
  // Bundles were removed from the product surface — pricing is à-la-carte only.
  // The `mode` state was kept until we're sure bundles won't come back; for now
  // the toggle is hidden and there is exactly one rendering path.
  const [bill, setBill] = useState<"monthly" | "yearly">("monthly");

  const { catalog, error } = usePricing();
  const interval = bill === "yearly" ? "year" : "month";
  const vm = buildPricingViewModel(catalog, interval);

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: C.bg,
        padding: "var(--section-pad, 96px) 0",
      }}
    >
      {/* Decorative gradient overlay */}
      <div
        style={{
          position: "absolute",
          inset: "-20% -10% auto -10%",
          height: "60%",
          background: [
            "radial-gradient(ellipse 50% 60% at 15% 30%, rgba(52,157,255,0.12), transparent 60%)",
            "radial-gradient(ellipse 55% 70% at 85% 20%, rgba(255,134,29,0.10), transparent 60%)",
            "radial-gradient(ellipse 60% 70% at 50% 80%, rgba(53,29,189,0.08), transparent 60%)",
          ].join(", "),
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 56px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: C.purple, marginBottom: 12 }}>
            03 · Pricing
          </div>
          <h2 style={{ fontSize: "clamp(32px, 3.8vw, 44px)", fontWeight: 800, letterSpacing: "-0.03em", color: C.navy, margin: "0 0 16px", lineHeight: 1.06 }}>
            Pay for what you need.
          </h2>
          <p style={{ fontSize: 17, color: C.textMuted, margin: "0 auto", maxWidth: "54ch", lineHeight: 1.6 }}>
            Pick the tools you need. Start free, upgrade in the app.
          </p>

          {/* Billing period toggle — bundle/ala toggle removed (bundles deprecated). */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, marginTop: 28 }}>
            <div role="group" aria-label="Billing period" style={{ display: "inline-flex", background: C.white, border: `1px solid ${C.muted}`, borderRadius: 9999, padding: 4, boxShadow: "var(--shadow-sm)" }}>
              <BillBtn active={bill === "monthly"} onClick={() => setBill("monthly")}>Monthly</BillBtn>
              <BillBtn active={bill === "yearly"} onClick={() => setBill("yearly")}>
                Yearly
                {vm && vm.yearlyDiscountPct > 0 && (
                  <SavePill active={bill === "yearly"}>−{vm.yearlyDiscountPct}%</SavePill>
                )}
              </BillBtn>
            </div>
          </div>
        </div>

        {/* Loading / error fallback */}
        {!vm && error && (
          <div style={{ textAlign: "center", color: C.textMuted, padding: "32px 0" }}>
            Couldn't load pricing right now. Please refresh the page.
          </div>
        )}

        {/* À la carte tools — bundle column removed (bundles deprecated). */}
        {vm && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, maxWidth: 1100, margin: "0 auto" }}>
              {vm.products.map((p) => {
                const display = PRODUCT_DISPLAY[p.id] ?? DEFAULT_DISPLAY;
                return (
                  <article
                    key={p.id}
                    style={{
                      background: C.white,
                      border: `1px solid ${C.muted}`,
                      borderRadius: 16,
                      padding: "22px 24px 24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 12, background: display.tint, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <ProductIcon type={display.icon} color={display.color} size={20} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: 0 }}>{p.displayName}</h4>
                        {display.sub && (
                          <p style={{ fontSize: 12, color: C.textMuted, margin: "2px 0 0", lineHeight: 1.4 }}>{display.sub}</p>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: C.successText, background: "#e9fbe9", padding: "3px 7px", borderRadius: 4, alignSelf: "flex-start" }}>
                      Free tier included
                    </span>
                    <div style={{ height: 1, background: C.muted, margin: "4px 0" }} />
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: C.navy, alignSelf: "flex-start", marginTop: 4 }}>€</span>
                      <span style={{ fontSize: 32, fontWeight: 800, color: C.navy, letterSpacing: "-0.025em", lineHeight: 1 }}>{p.priceEur}</span>
                      <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500, marginLeft: 3 }}>/ month</span>
                    </div>
                    <Link
                      to={`/account/billing?product=${p.id}&action=buy`}
                      style={{ fontSize: 13, fontWeight: 700, color: C.purple, display: "inline-flex", alignItems: "center", gap: 6, marginTop: "auto", textDecoration: "none" }}
                    >
                      Choose a plan →
                    </Link>
                  </article>
                );
              })}
            </div>

            {/* Bundle upsell removed (bundles deprecated). */}
          </>
        )}

        {/* À la carte loading skeleton */}
        {!vm && !error && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, maxWidth: 1100, margin: "0 auto" }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ background: C.white, border: `1px solid ${C.muted}`, borderRadius: 16, padding: "22px 24px 24px", height: 200 }}>
                <PriceSkeleton />
              </div>
            ))}
          </div>
        )}

        {/* Reassurance row */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 40,
            flexWrap: "wrap",
            marginTop: 48,
            paddingTop: 32,
            borderTop: `1px solid ${C.muted}`,
            maxWidth: 1100,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <ReassureItem icon="shield">EU data residency (Frankfurt)</ReassureItem>
          <ReassureItem icon="lock">GDPR &amp; ISO-27001</ReassureItem>
          <ReassureItem icon="refresh">Cancel &amp; export anytime</ReassureItem>
          <ReassureItem icon="chat">Human support &lt; 4h</ReassureItem>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

function PriceSkeleton({ featured }: { featured?: boolean }) {
  return (
    <span
      role="status"
      aria-label="Loading price"
      style={{
        display: "inline-block",
        width: 60,
        height: 54,
        borderRadius: 6,
        background: featured ? "rgba(255,255,255,0.18)" : C.mutedSoft,
        verticalAlign: "middle",
      }}
    />
  );
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        font: "inherit",
        fontSize: 14,
        fontWeight: 600,
        color: active ? C.white : C.textMuted,
        background: active ? C.navy : "transparent",
        boxShadow: active ? "0 2px 8px rgba(38,31,89,0.3)" : "none",
        border: 0,
        padding: "10px 22px",
        borderRadius: 9999,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        transition: "all 180ms cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      {children}
    </button>
  );
}

function BillBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        font: "inherit",
        fontSize: 13,
        fontWeight: 600,
        color: active ? C.white : C.textMuted,
        background: active ? C.navy : "transparent",
        border: 0,
        padding: "8px 18px",
        borderRadius: 9999,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        transition: "all 180ms cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      {children}
    </button>
  );
}

function SavePill({ active, children }: { active?: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        padding: "2px 7px",
        borderRadius: 4,
        background: active ? "rgba(255,134,29,0.25)" : C.orangeLight,
        color: active ? "#FFD2A8" : C.orange,
      }}
    >
      {children}
    </span>
  );
}

function TierCard({ featured, children }: { featured?: boolean; children: React.ReactNode }) {
  return (
    <article
      style={{
        position: "relative",
        background: featured
          ? `linear-gradient(160deg, ${C.navy} 0%, #1b1448 60%, ${C.purple} 120%)`
          : C.white,
        color: featured ? C.white : "inherit",
        border: featured ? "1px solid transparent" : `1px solid ${C.muted}`,
        borderRadius: 20,
        padding: "32px 30px 36px",
        display: "flex",
        flexDirection: "column",
        gap: 22,
        boxShadow: featured
          ? "0 24px 60px rgba(53,29,189,0.28), 0 8px 16px rgba(53,29,189,0.14)"
          : "var(--shadow-sm)",
        overflow: "hidden",
      }}
    >
      {featured && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: [
              "radial-gradient(ellipse 70% 50% at 80% 0%, rgba(52,157,255,0.35), transparent 60%)",
              "radial-gradient(ellipse 50% 50% at 10% 100%, rgba(255,134,29,0.22), transparent 60%)",
            ].join(", "),
            pointerEvents: "none",
          }}
        />
      )}
      {featured
        ? React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<{ style?: React.CSSProperties }>, {
                  style: { ...(child.props as { style?: React.CSSProperties }).style, position: "relative", zIndex: 1 },
                })
              : child,
          )
        : children}
    </article>
  );
}

function TierTop({ name, badge, featured }: { name: string; badge: string; featured?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: featured ? "rgba(255,255,255,0.7)" : C.textMuted }}>
        {name}
      </span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          padding: "4px 8px",
          borderRadius: 9999,
          background: featured ? "rgba(255,134,29,0.22)" : C.mutedSoft,
          color: featured ? "#FFC591" : C.textMuted,
        }}
      >
        {badge}
      </span>
    </div>
  );
}

function TierDivider({ featured }: { featured?: boolean }) {
  return <div style={{ height: 1, background: featured ? "rgba(255,255,255,0.12)" : C.muted }} />;
}

function TierFeatures({ items, featured }: { items: string[]; featured?: boolean }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: featured ? "rgba(255,255,255,0.92)" : C.textStrong, lineHeight: 1.5 }}>
          <CheckIcon color={featured ? C.white : "currentColor"} />
          {item}
        </li>
      ))}
    </ul>
  );
}

function DeltaSticker({ featured, children }: { featured?: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        fontWeight: 700,
        padding: "6px 12px",
        borderRadius: 9999,
        background: featured ? "rgba(180,255,180,0.14)" : "#e9fbe9",
        color: featured ? "#B4FFB4" : C.successText,
        border: `1px solid ${featured ? "rgba(180,255,180,0.25)" : "#bef0bf"}`,
        width: "fit-content",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
      </svg>
      {children}
    </span>
  );
}

function ReassureItem({ icon, children }: { icon: string; children: React.ReactNode }) {
  const iconPaths: Record<string, React.ReactNode> = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    refresh: <><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>,
    chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 13, color: C.textMuted, fontWeight: 500 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {iconPaths[icon]}
      </svg>
      {children}
    </span>
  );
}
